/**
 * Der Hinweis zur Bildrate — eine ruhige Zeile in Einstellungen → Erweitert und EINE
 * Meldung am Tag, wenn das Gerät mit halber Bildrate zeichnet. (02.10.2026, pre.3600)
 *
 * Wann „halb“ gilt, entscheidet lib/bildrate.ts (mit den Zahlen des Tages). Hier steht nur, was
 * man davon sieht:
 *   - Einstellungen: der Stand, solange die Seite offen ist (wechselt mit, `role=status`).
 *   - Meldung: höchstens einmal je Kalendertag und Gerät, und nur bei sichtbarer Seite. Kein Ton,
 *     kein Fenster, kein Knopf — er soll wissen, woran es liegt, nicht etwas wegklicken müssen.
 */
import { beiBildrate, bildrateStand, type BildrateStand } from "./bildrate.js";

const BILDRATE_TEXTE: Record<BildrateStand, string> = {
  unbekannt: "Noch nicht gemessen. Nach ein paar Sekunden Scrollen steht hier, wie flüssig dein Gerät gerade zeichnet.",
  voll: "Flüssig: Dein Gerät zeichnet mit voller Bildrate.",
  halb:
    "Halbe Bildrate: Dein Gerät zeichnet gerade nur rund 30 statt 60 Bilder pro Sekunde, meist wegen des Stromsparmodus. " +
    "Scrollen wirkt dadurch ruckelig. Das ist kein Fehler der App; ohne Stromsparmodus wird es wieder flüssig.",
};

const BILDRATE_MELDUNG =
  "Dein Gerät zeichnet gerade mit halber Bildrate (Stromsparmodus?). Scrollen wirkt dadurch ruckelig. Das ist kein Fehler der App.";

/** Unter diesem Schlüssel merkt sich das Gerät den Tag der letzten Meldung. */
export const MELDUNG_TAG_SCHLUESSEL = "voice-v8.bildrate-hinweis-tag";
const MELDUNG_MS = 8000;

/** Die Karte für „Erweitert“. Sie folgt dem Stand, solange sie im Dokument hängt. */
export function bildrateKarte(): HTMLElement {
  const karte = document.createElement("section");
  karte.className = "settings-card bildrate-karte";
  const titel = document.createElement("h3");
  titel.textContent = "Bildrate beim Scrollen";
  const zeile = document.createElement("p");
  zeile.className = "settings-hint bildrate-stand";
  zeile.setAttribute("role", "status");
  zeile.setAttribute("aria-live", "polite");
  const zeige = (stand: BildrateStand): void => {
    zeile.dataset.stand = stand;
    zeile.textContent = BILDRATE_TEXTE[stand];
  };
  zeige(bildrateStand());
  const abmelden = beiBildrate((stand) => {
    if (!karte.isConnected) {
      abmelden();
      return;
    }
    zeige(stand);
  });
  karte.append(titel, zeile);
  return karte;
}

function heute(): string {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/** Heute schon gemeldet? Ohne Speicher (privates Fenster, gesperrt): nein — dann höchstens einmal je Laden. */
function schonGemeldet(): boolean {
  try {
    return localStorage.getItem(MELDUNG_TAG_SCHLUESSEL) === heute();
  } catch {
    return false;
  }
}

function merkeGemeldet(): void {
  try {
    localStorage.setItem(MELDUNG_TAG_SCHLUESSEL, heute());
  } catch {
    /* ohne Speicher */
  }
}

let laeuft = false;

/** Die Meldung einschalten (einmal, aus app.ts). */
export function starteBildrateHinweis(): void {
  if (laeuft || typeof window === "undefined") return;
  laeuft = true;
  let gemeldetSeitLaden = false;
  beiBildrate((stand) => {
    if (stand !== "halb" || gemeldetSeitLaden || schonGemeldet()) return;
    if (document.visibilityState !== "visible") return;
    gemeldetSeitLaden = true;
    merkeGemeldet();
    window.dispatchEvent(new CustomEvent("voice-v8:toast", { detail: { message: BILDRATE_MELDUNG, kind: "info", ms: MELDUNG_MS } }));
  });
}
